import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, getToken, removeToken, removeExternalSession } from '../utils/api';

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, password_confirmation: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Проверка авторизации при загрузке
  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const response = await authAPI.getUser();
          if (response.success && response.user) {
            setUser(response.user);
          }
        } catch (error) {
          console.error('Ошибка проверки авторизации:', error);
          const status = (error as { status?: number })?.status;
          if (status === 401) {
            removeToken();
            removeExternalSession();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authAPI.login(email, password);
    if (response.success && response.user) {
      setUser(response.user);
    } else {
      throw new Error('Ошибка входа');
    }
  };

  const register = async (name: string, email: string, password: string, password_confirmation: string) => {
    const response = await authAPI.register(name, email, password, password_confirmation);
    if (response.success && response.user) {
      setUser(response.user);
    } else {
      throw new Error('Ошибка регистрации');
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Ошибка выхода:', error);
    } finally {
      setUser(null);
      removeToken();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
